import type { MailEvidence, PurchaseClaim, PurchaseClaimDelivery } from "./creditReview.js";
import { formatAgo, formatDateTime, validTimestamp } from "./format.js";

const codes: Record<string, string> = {
  pending: "Waiting for an invitation",
  needs_contact: "No payer email on the purchase",
  resend_requested: "Resend requested",
  invited: "Invitation created",
  invitation_pending: "Invitation outstanding, delivery not confirmed",
  accepted: "Payer verified the invitation",
  review_required: "Staff review required",
  retry_later: "Delivery retry scheduled",
  delivery_failed: "Invitation delivery failed",
  activated: "Purchase activated",
  waiting_for_verification: "Waiting for the payer to verify their email",
  waiting_for_student: "Learner needs a booking match",
  waiting_for_access: "Learner needs access approval",
  selection_conflict: "Learner selection conflicts with the booking",
  purchase_identity_conflict: "Purchase identity conflict",
  payer_contact_changed: "Payer contact changed",
  purchase_evidence_changed: "Purchase evidence changed",
  redemption_authority_conflict: "Credit authority conflict",
  multiple_matching_accounts: "Multiple matching accounts",
  claim_account_changed: "Claim account changed",
  claim_mail_unavailable: "Claim email unavailable",
  invitations_disabled: "Invitations are disabled",
  delivery_retry_exhausted: "Delivery retries exhausted",
  retry_exhausted: "Recovery retries exhausted",
  claim_mail_failed: "Claim email failed",
  contact_recovered: "Payer contact recovered",
  assigned: "Learner assigned",
  action_required: "Customer action required",
  unavailable: "Preference unavailable",
  resend_too_soon: "Resend requested too soon",
};
const conflicts = new Set([
  "purchase_identity_conflict", "payer_contact_changed", "purchase_evidence_changed",
  "redemption_authority_conflict", "multiple_matching_accounts", "claim_account_changed", "selection_conflict",
]);
const failures = new Set([
  "delivery_failed", "retry_later", "delivery_retry_exhausted", "retry_exhausted", "invitations_disabled", "claim_mail_failed",
]);

const RECENT_MAIL_WINDOW_MS = 15 * 60_000;

function pendingMailIsRecent(mail: MailEvidence, now: number): boolean {
  if (!validTimestamp(mail.updatedAt)) return false;
  const age = now - Date.parse(mail.updatedAt);
  return age >= 0 && age < RECENT_MAIL_WINDOW_MS;
}

function stalledMailText(mail: MailEvidence): string {
  return `${mail.status === "queued" ? "Queued" : "Sending"} since ${formatDateTime(mail.updatedAt, false, true)}. ${mail.status === "queued" ? "Not sent yet" : "Send not confirmed"}. Check the mail worker.`;
}

export function humaniseClaimCode(code: string | null | undefined): string {
  return code ? codes[code] ?? `Needs review (${code})` : "Unknown";
}

export function mailStatusText(mail: MailEvidence | null | undefined): string {
  if (!mail) return "No email record";
  switch (mail.status) {
    case "sent": return "Sent to email provider, delivery not confirmed";
    case "delivered": return "Delivered";
    case "queued": return "Queued";
    case "sending": return "Sending";
    case "failed": return "Failed";
    case "bounced": return "Bounced";
    case "complained": return "Complaint received";
    default: return `Needs review (${String(mail.status)})`;
  }
}

export function mailEvidenceText(mail: MailEvidence | null | undefined): string {
  const at = mail?.status === "sent" ? mail.sentAt ?? mail.updatedAt : mail?.updatedAt;
  return `${mailStatusText(mail)}${mail ? ` · ${formatDateTime(at, false, true)}` : ""}`;
}

type Action = "resend" | "assign" | "booking" | "refresh" | "reviews" | null;
export interface ClaimNextStep {
  rule: number;
  text: string;
  tone: "neutral" | "info" | "warning";
  primary: Action;
  canAssign: boolean;
  resend: "allowed" | "disabled" | "hidden";
  resendReason: string | null;
}

/** Read-only, deterministic mapping. The caller supplies time for expiry and cooldown checks. */
export function purchaseClaimState(row: PurchaseClaim, delivery: PurchaseClaimDelivery | undefined, now: number) {
  // Evidence from a different payment must never be attached to this card.
  const evidence = delivery?.paymentId === row.amelia_payment_id ? delivery : undefined;
  const claim = evidence?.claim;
  const invitation = claim?.invitation;
  const mails = [claim?.invitationMail, claim?.actionMail].filter((mail): mail is MailEvidence => !!mail);
  const rowCodes = [row.claim_invite_status, row.claim_invite_error, row.claim_recovery_status, row.claim_recovery_error]
    .filter((code): code is string => !!code);
  const live = !!invitation && !invitation.revokedAt && validTimestamp(invitation.expiresAt) && Date.parse(invitation.expiresAt) > now;
  const lastEmail = mailEvidenceText(claim?.invitationMail);
  const email = evidence === undefined
    ? { line: `Email status unavailable.${row.claim_invite_status === "invitation_pending" ? " Invitation outstanding, delivery not confirmed." : ""}` }
    : { line: claim?.actionMail && claim.verifiedAt
        ? `Choose-learner email: ${mailEvidenceText(claim.actionMail)}`
        : `Invitation email: ${mailEvidenceText(claim?.invitationMail)}` };
  const step = (rule: number, text: string, primary: Action = null, tone: ClaimNextStep["tone"] = "neutral", canAssign = true,
    resend: ClaimNextStep["resend"] = "allowed", resendReason: string | null = null): ClaimNextStep =>
    ({ rule, text, primary, tone, canAssign, resend, resendReason });

  let nextStep: ClaimNextStep;
  const conflict = rowCodes.find((code) => conflicts.has(code));
  const mismatch = claim && claim.bookingId !== row.amelia_purchase_booking_id;
  const unknown = rowCodes.find((code) => !codes[code]);
  const failure = rowCodes.find((code) => failures.has(code));
  const pendingMail = mails.find((mail) => mail.status === "queued" || mail.status === "sending");
  const deliveryRecentlyStarted = validTimestamp(claim?.deliveryStartedAt) &&
    now - Date.parse(claim.deliveryStartedAt) >= 0 && now - Date.parse(claim.deliveryStartedAt) < 5 * 60_000;
  if (row.payment_reversal_hold_at && !row.payment_reversal_cleared_at) {
    nextStep = step(1, "Payment reversal under review. Don't assign.", "reviews", "warning", false, "hidden");
  } else if (mismatch || conflict) {
    nextStep = step(2, `Staff review needed: ${humaniseClaimCode(mismatch ? "purchase_identity_conflict" : conflict)}.`, "booking", "warning", true, "hidden");
  } else if (rowCodes.includes("needs_contact") || !row.claim_payer_email?.trim()) {
    nextStep = step(3, "No payer email on the purchase. Check Amelia.", "resend", "warning");
  } else if (mails.some((mail) => mail.status === "bounced" || mail.status === "complained") || rowCodes.includes("claim_mail_unavailable")) {
    nextStep = step(4, "Email can't be delivered to this address. Contact the family another way.", null, "warning", true, "disabled",
      "Resending cannot resolve a bounce or complaint. Verify the contact through another channel.");
  } else if (rowCodes.includes("resend_too_soon")) {
    const earliest = validTimestamp(invitation?.mailQueuedAt) ? new Date(Date.parse(invitation.mailQueuedAt) + 10 * 60_000).toISOString() : row.claim_invite_next_at;
    nextStep = step(14, `Resend requested too soon. ${validTimestamp(earliest) ? `Try again after ${formatDateTime(earliest, false, true)}.` : "Refresh to check when another resend is possible."}`, "refresh", "info");
  } else if (mails.some((mail) => mail.status === "failed") || failure) {
    const disabled = rowCodes.includes("invitations_disabled");
    const actionFailed = claim?.actionMail?.status === "failed";
    const invitationFailed = claim?.invitationMail?.status === "failed";
    const failedCopy = actionFailed ? invitationFailed
      ? "Invitation and choose-learner emails didn't go out."
      : "Choose-learner email didn't go out."
      : "Invitation didn't go out.";
    nextStep = step(5, `${failedCopy}${disabled ? " Invitations are disabled." : ""}`, disabled ? null : actionFailed && claim?.verifiedAt ? "assign" : "resend", "warning", true,
      disabled ? "disabled" : "allowed", disabled ? "Invitations are disabled. Staff must review the mail configuration." : null);
  } else if (claim && !claim.verifiedAt && !invitation && mails.length === 0) {
    nextStep = step(6, "Invitation never created.", "resend", "warning");
  } else if (invitation && !invitation.acceptedAt && (invitation.revokedAt || (validTimestamp(invitation.expiresAt) && Date.parse(invitation.expiresAt) <= now))) {
    nextStep = step(7, "Invitation expired.", "resend", "warning");
  } else if (unknown) {
    nextStep = step(14, humaniseClaimCode(unknown), "booking", "warning");
  } else if ((!claim?.verifiedAt && pendingMail) || deliveryRecentlyStarted) {
    nextStep = deliveryRecentlyStarted || (pendingMail && pendingMailIsRecent(pendingMail, now))
      ? step(8, "Sending…", null, "info", true, "hidden")
      // A resend cannot fix a stalled outbox: it revokes the queued link and
      // queues another mail behind it. Keep resend available, not promoted.
      : step(8, stalledMailText(pendingMail!), null, "warning");
  } else if (claim?.verifiedAt && !claim.selectedAt) {
    // A queued follow-up means the payer has already acted, not that their invitation is still queued.
    const action = claim.actionMail;
    nextStep = !action
      ? step(9, "Payer verified their email. Staff can match the booking and assign the purchase.", "assign", "info")
      : (action.status === "queued" || action.status === "sending") && !pendingMailIsRecent(action, now)
        ? step(9, `Payer verified their email. Choose-learner email: ${stalledMailText(action)}`, "assign", "warning")
        : step(9, "Payer verified their email, waiting for them to choose the learner.", "assign", "info");
  } else if (claim?.selectedAt || rowCodes.includes("waiting_for_student") || rowCodes.includes("waiting_for_access")) {
    nextStep = step(10, "Learner chosen, needs booking match or access approval.", "booking", "info");
  } else if (mails.some((mail) => mail.status === "delivered") && !claim?.verifiedAt) {
    nextStep = step(11, "Waiting for payer to use the invitation link.", null, "info");
  } else if (mails.some((mail) => mail.status === "sent") && !claim?.verifiedAt) {
    nextStep = step(12, "Waiting for payer to use the invitation link.");
  } else if (evidence && claim === null && row.claim_invite_status === "pending" && !row.claim_invite_error && !row.claim_recovery_error) {
    nextStep = step(14, "Waiting for the invitation worker.");
  } else if (evidence === undefined) {
    nextStep = step(13, "Waiting state unknown.", "refresh");
  } else {
    nextStep = step(14, `Needs review: ${humaniseClaimCode(row.claim_recovery_status ?? row.claim_invite_error ?? row.claim_invite_status)}.`, "booking", "warning");
  }
  if (nextStep.resend === "allowed" && mails.some((mail) => mail.status === "bounced" || mail.status === "complained")) {
    nextStep = { ...nextStep, primary: nextStep.primary === "resend" ? "booking" : nextStep.primary, resend: "disabled",
      resendReason: "Resending cannot resolve a bounce or complaint. Verify the contact through another channel." };
  }
  return {
    purchase: {
      paid: formatDateTime(row.paid_at), imported: formatDateTime(row.created_at, true),
      age: validTimestamp(row.paid_at) ? formatAgo(row.paid_at, now) : null,
    },
    invitation: {
      live,
      // A deployment without diagnostics cannot establish that a prior link is dead.
      confirmResend: live || (evidence === undefined && ["invited", "invitation_pending", "accepted"].includes(row.claim_invite_status ?? "")),
      lastEmail: evidence === undefined ? "Email status unavailable" : lastEmail,
    },
    email,
    nextStep,
  };
}
